import { ArrowLeftOutlined, PlusCircleOutlined, TrophyOutlined } from '@ant-design/icons'
import { Alert, Button, Col, Radio, Row, Table, Tooltip } from 'antd'
import { RadioChangeEvent } from 'antd/lib/radio'
import { ColumnProps } from 'antd/lib/table'
import classNames from 'classnames'
import * as R from 'ramda'
import React from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirebase, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import { withHandlers } from 'recompose'
import { compose } from 'redux'

import { Category, CategoryId, TournamentCategory } from '../../../schemas/Category'
import { TenantId } from '../../../schemas/Tenant'
import { TournamentId } from '../../../schemas/Tournament'

import { SHOW_NOTIFICATION } from '../../../constants/ActionTypes'
import { ERROR, SUCCESS } from '../../../constants/notifications'
import { sortCategoriesByDefaultOrder } from '../../../helpers/compare'
import {
  batchReprocessTournamentPlayers,
  findInOrdered,
  fsCollections,
  makeInBatchWithFirebase,
} from '../../../helpers/firestore'
import { withRemovedCategory } from '../../../helpers/players'
import routes from '../../../helpers/routes'
import { defaultLanguage, languages } from '../../../shared/languages'
import { DeleteButtonWithConfirmation } from '../../ActionWithConfirmation'
import authorize from '../../higher-order/authorize'
import TournamentCategoryForm from './TournamentCategoryForm'

import './TournamentCategoriesList.css'

type RemoveCategoryProps = {
  disabled: boolean
  removeCategoryAction(): void
}

const RemoveCategory: React.FunctionComponent<RemoveCategoryProps> = ({
  removeCategoryAction,
  disabled,
}) => (
  <DeleteButtonWithConfirmation
    title="Are you sure to remove this category from tournament?"
    popupPlacement="left"
    onConfirmAction={removeCategoryAction}
    disabled={disabled}
  />
)

const columnConfig = (
  tenant: TenantId,
  tournamentId: TournamentId,
  language: string,
  removeCategory: (categoryId: CategoryId) => void,
  setMainCategory: (category: TournamentCategory) => (isMain: boolean) => void,
  actionsDisabled: boolean,
) => [
  {
    title: 'Main',
    key: 'main',
    render: (text, record) => {
      const onMainChangeAction = (event: RadioChangeEvent) =>
        setMainCategory(record)(event.target.checked)

      return (
        <Radio onChange={onMainChangeAction} checked={record.main} disabled={actionsDisabled} />
      )
    },
  },
  {
    title: '',
    key: 'championship',
    render: (text, record) =>
      record.ranking ? (
        <Tooltip title={`${record.ranking.name} [${record.ranking.points}]`}>
          <TrophyOutlined />
        </Tooltip>
      ) : (
        ''
      ),
  },
  {
    title: 'Name',
    key: `name:${language}`,
    render: (text, record) => (
      <Link
        className={classNames({ 'disabled-link': actionsDisabled })}
        to={routes.editTournamentCategory(tenant, tournamentId, record.id)}>
        {record[`name:${language}`]}
      </Link>
    ),
  },
  {
    title: 'Level Filter',
    key: 'level',
    dataIndex: 'level',
  },
  {
    title: 'Gender Filter',
    key: `gender`,
    dataIndex: 'gender',
  },
  {
    title: 'Age Filter',
    key: 'age',
    render: (text, record) => {
      const minAge = record.minAge
      const maxAge = record.maxAge

      if (minAge && maxAge) {
        return `${minAge} - ${maxAge}`
      }
      if (minAge) {
        return `≥ ${minAge}`
      }
      if (maxAge) {
        return `≤ ${maxAge}`
      }

      return undefined
    },
  },
  {
    key: 'action',
    width: '2em',
    render: (text, record) => {
      const removeCategoryAction = () => removeCategory(record.id)

      return (
        !record.main && (
          <RemoveCategory disabled={actionsDisabled} removeCategoryAction={removeCategoryAction} />
        )
      )
    },
  },
]

const globalCategoriesColumns = (importCategory: FIXME): Array<ColumnProps<Category>> => [
  {
    key: 'import',
    width: '2em',
    render: (text, record) => {
      const onImportCategory = () => importCategory(record)

      return (
        <Button
          icon={<ArrowLeftOutlined />}
          shape="circle"
          size="small"
          onClick={onImportCategory}
        />
      )
    },
  },

  {
    key: 'name',
    width: '2em',
    title: 'Name',
    render: (text, record) => record['name:en'],
  },
]

type CategoriesListProps = {
  tenant: TenantId
  tournamentId: TournamentId
  categoryId?: CategoryId
  newCategory: boolean
  category?: TournamentCategory
  globalCategories: FIXME[]
  tournamentCategories: TournamentCategory[]
  setMainCategory(category: TournamentCategory): (isMain: boolean) => void
  removeCategory(categoryId: CategoryId): void
  importCategory(category: Category): void
}

const CategoriesList: React.FunctionComponent<CategoriesListProps> = ({
  globalCategories,
  tournamentCategories,
  importCategory,
  removeCategory,
  setMainCategory,
  categoryId,
  category,
  newCategory,
  tournamentId,
  tenant,
}) => {
  // TODO take from locale/config
  const formOpen = Boolean(categoryId) || newCategory
  const form = formOpen ? (
    <React.Fragment>
      <h3>{newCategory ? 'Add Category' : 'Edit Category'}</h3>
      <TournamentCategoryForm
        tenant={tenant}
        tournamentId={tournamentId}
        categoryId={categoryId}
        category={category}
        languages={languages}
        previousLocation={routes.manageTournamentCategories(tenant, tournamentId)}
      />
    </React.Fragment>
  ) : null

  const isLoading = !isLoaded(tournamentCategories)

  const tournamentCategoriesSorted = sortCategoriesByDefaultOrder(tournamentCategories || [])

  const mainCategory = R.find(R.prop('main'), tournamentCategoriesSorted)

  const availableCategories = (globalCategories || []).filter((globalCategory) =>
    R.none(
      (tournamentCategory) => globalCategory.id === tournamentCategory.id,
      tournamentCategories || [],
    ),
  )

  return (
    <div className="TournamentCategories">
      <div className="TournamentCategories__header">
        <h3>Manage Categories</h3>
        <Button.Group>
          <Link to={routes.newTournamentCategory(tenant, tournamentId)}>
            <Button icon={<PlusCircleOutlined />}>Add</Button>
          </Link>
        </Button.Group>
      </div>
      <Row>
        <Col span={16}>
          <div style={{ marginLeft: 10, marginRight: 5 }}>
            {form}
            {!isLoading && !mainCategory && (
              <Alert type="error" message="Main category is not selected" showIcon />
            )}
            <Table
              dataSource={tournamentCategoriesSorted}
              columns={columnConfig(
                tenant,
                tournamentId,
                defaultLanguage,
                removeCategory,
                setMainCategory,
                formOpen,
              )}
              rowKey="id"
              loading={isLoading}
              size="small"
              pagination={false}
            />
          </div>
        </Col>
        <Col span={8}>
          {!form && (
            <div style={{ marginLeft: 5, marginRight: 10 }}>
              <Table
                dataSource={availableCategories}
                columns={globalCategoriesColumns(importCategory)}
                rowKey="id"
                loading={!isLoaded(globalCategories)}
                size="small"
                pagination={false}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default compose<FIXME>(
  authorize('admin'),
  withFirebase,
  withFirestore,
  withRouter,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.globalCategories(tenant) },
    { collection: fsCollections.tournamentCategories(tenant, tournamentId) },
    { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
    { collection: fsCollections.tournaments(tenant), doc: tournamentId },
    { collection: fsCollections.globalPlayers(tenant) },
  ]),
  connect(({ firestore }: FIXME, { tenant, tournamentId, categoryId }: FIXME) => ({
    globalCategories: firestore.ordered[fsCollections.globalCategories(tenant)],
    tournamentCategories:
      firestore.ordered[fsCollections.tournamentCategories(tenant, tournamentId)],
    tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
    globalPlayers: firestore.ordered[fsCollections.globalPlayers(tenant)],
    tournament: findInOrdered(firestore.ordered[fsCollections.tournaments(tenant)], tournamentId),
  })),
  withHandlers({
    deleteSuccess: ({ dispatch, history, tenant, tournamentId }: FIXME) => (leaveEdit) => {
      dispatch({ type: SHOW_NOTIFICATION, message: 'Category deleted', kind: SUCCESS })
      if (leaveEdit) {
        history.push(routes.manageTournamentCategories(tenant, tournamentId))
      }
    },
    deleteError: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error('Failed to delete category', err)
      dispatch({ type: SHOW_NOTIFICATION, message: 'Failed to delete category', kind: ERROR })
    },
    inBatchBound: makeInBatchWithFirebase,
  }),
  withHandlers({
    removeCategory: ({
      inBatchBound,
      tenant,
      tournamentId,
      tournamentPlayers,
      match,
      deleteSuccess,
      deleteError,
    }) => (categoryId) =>
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(
          fsCollections.tournamentCategories(tenant, tournamentId),
        )
        batch.delete(collection.doc(categoryId))

        const updatedTournamentPlayers = withRemovedCategory(categoryId, tournamentPlayers)
        const tournamentPlayersCollection = firestore.collection(
          fsCollections.tournamentPlayers(tenant, tournamentId),
        )
        updatedTournamentPlayers.forEach((tournamentPlayer) =>
          batch.set(tournamentPlayersCollection.doc(tournamentPlayer.id), tournamentPlayer),
        )
      })
        .then((res) =>
          deleteSuccess(
            match.url === routes.editTournamentCategory(tenant, tournamentId, categoryId),
          ),
        )
        .catch(deleteError),
    setMainCategory: ({ inBatchBound, tenant, tournamentId, tournamentCategories }) => (
      category,
    ) => (isMain: boolean) =>
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(
          fsCollections.tournamentCategories(tenant, tournamentId),
        )
        const mains = R.filter(R.prop('main'), tournamentCategories)

        mains.forEach((cat: FIXME) => batch.set(collection.doc(cat.id), { ...cat, main: false }))
        batch.set(collection.doc(category.id), { ...category, main: isMain })
      }),
  }),
  withHandlers({
    upsertSuccess: ({ dispatch }) => () => {
      dispatch({ type: SHOW_NOTIFICATION, message: 'Category saved', kind: SUCCESS })
    },
    upsertError: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error('Failed to save category', err)
      dispatch({ type: SHOW_NOTIFICATION, message: 'Failed to save category', kind: ERROR })
    },
    upsertCategory: ({
      inBatchBound,
      tournamentId,
      tenant,
      globalPlayers,
      tournament,
      tournamentCategories,
      tournamentPlayers,
    }) => (category) =>
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(
          fsCollections.tournamentCategories(tenant, tournamentId),
        )
        const categoryReference = category.id ? collection.doc(category.id) : collection.doc()

        batch.set(categoryReference, {
          ...category,
          main:
            tournamentCategories.length === 0 ||
            (tournamentCategories.length === 1 && tournamentCategories[0].id === category.id),
        })

        // we need to add new/updated category to the collection passed to players reprocessing
        const updatedTournamentCategories = [
          ...tournamentCategories.filter(
            (tournamentCategory) => tournamentCategory.id !== category.id,
          ),
          { id: categoryReference.id, ...category },
        ]

        batchReprocessTournamentPlayers({
          tenant,
          tournamentId,
          tournament,
          globalPlayers,
          tournamentCategories: updatedTournamentCategories,
          tournamentPlayers,
        })(firestore, batch)
      }),
  }),
  withHandlers({
    importCategory: ({ upsertCategory, upsertError, upsertSuccess }) => (category) =>
      upsertCategory(category)
        .then((res) => upsertSuccess(res))
        .catch((err) => upsertError(err)),
  }),
)(CategoriesList)



// WEBPACK FOOTER //
// ./src/components/tournament/management/TournamentCategoriesList.tsx